import type { AuthTokens, LoginResponse } from './types';
import type { ApiClient } from './client';

export const authMethods = {
async login(username: string, password: string): Promise<LoginResponse> {
      try {
        const response = await this.makeRequest<LoginResponse>('/login.php', {
          method: 'POST',
          body: JSON.stringify({
            username: this.sanitizeString(username),
            password: password // Don't sanitize password as it may contain special chars
          }),
        });

        if (response.success && response.data) {
          // Store authentication tokens with user data
          const tokens: AuthTokens = {
            accessToken: response.data.token,
            expiresAt: response.data.expires_at,
            refreshToken: response.data.refresh_token,
            user: response.data.user
          };
          this.saveAuthTokens(tokens);
          return response.data;
        } else {
          throw new Error(response.error || 'Login failed');
        }
      } catch (error: any) {
        console.error('Login error:', error);
        throw new Error(error.message || 'Login failed. Please check your credentials.');
      }
    },


    async logout(): Promise<void> {
      const request = this.authTokens
        ? this.makeRequest('/auth/logout.php', { method: 'POST' }) : Promise.resolve();
      // End the local session immediately so a delayed refresh cannot restore it.
      this.clearAuthTokens();
      try { await request; } catch (error) { console.error('Logout request failed:', error); }
    },

    async refreshToken(): Promise<AuthTokens | null> {
      if (this.pendingRefresh) return this.pendingRefresh;
      const current = this.authTokens;
      const generation = this.sessionGeneration;
      if (!current) return null;
      const pending = (async () => {
        try {
          const response = await this.makeRequest<{ token: string; expires_at: string; refresh_token?: string; user?: AuthTokens['user'] }>('/auth/refresh.php', {
            method: 'POST',
            body: JSON.stringify(current.refreshToken ? { refresh_token: current.refreshToken } : {}),
          });
          if (generation !== this.sessionGeneration || this.authTokens?.accessToken !== current.accessToken) return null;
          if (response.success && response.data) {
            const tokens: AuthTokens = {
              accessToken: response.data.token,
              expiresAt: response.data.expires_at,
              refreshToken: response.data.refresh_token ?? current.refreshToken,
              user: response.data.user ?? current.user,
            };
            this.saveAuthTokens(tokens);
            return tokens;
          }
        } catch (error) {
          const rejection = error as { status?: number; code?: string };
          if (generation === this.sessionGeneration && this.authTokens?.accessToken === current.accessToken &&
              (rejection.status === 401 || rejection.code === 'refresh_failed' || rejection.code === 'user_inactive')) {
            this.clearAuthTokens();
          }
        }
        return null;
      })();
      this.pendingRefresh = pending;
      try { return await pending; }
      finally { if (this.pendingRefresh === pending) this.pendingRefresh = null; }
    },

    async register(userData: {
      registrationCode: string;
      username: string;
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      country: string;
      stateProvince: string;
      gender: string;
      birthDate: string;
      instagramUsername?: string;
      facebookUrl?: string;
      websiteUrl?: string;
      bio?: string;
      acceptedTerms?: boolean;
      marketingOptIn?: boolean;
    }): Promise<any> {
      const sanitizedData = {
        registration_code: this.sanitizeString(userData.registrationCode),
        username: this.sanitizeString(userData.username),
        email: this.sanitizeString(userData.email).toLowerCase(),
        password: userData.password, // Don't sanitize
        first_name: this.sanitizeString(userData.firstName),
        last_name: this.sanitizeString(userData.lastName),
        country: this.sanitizeString(userData.country),
        state_province: this.sanitizeString(userData.stateProvince),
        gender: this.sanitizeString(userData.gender),
        birth_date: this.sanitizeDate(userData.birthDate),
        instagram_username: this.sanitizeString(userData.instagramUsername || ''),
        facebook_url: this.sanitizeUrl(userData.facebookUrl || ''),
        website_url: this.sanitizeUrl(userData.websiteUrl || ''),
        bio: this.sanitizeString(userData.bio || ''),
        accepted_terms: Boolean(userData.acceptedTerms),
        marketing_opt_in: Boolean(userData.marketingOptIn),
      };

      try {
        const response = await this.makeRequest('/auth/register.php', {
          method: 'POST',
          body: JSON.stringify(sanitizedData),
        });

        if (!response.success) {
          throw new Error(response.error || 'Registration failed');
        }

        return response;
      } catch (error: any) {
        console.error('Registration failed:', error);
        const err = new Error(error.message || 'An error occurred during registration.');
        Object.assign(err, { code: error.code, status: error.status });
        throw err;
      }
    }
} satisfies ThisType<ApiClient>;
